import { useCustom, useCustomMutation, useNavigation } from "@refinedev/core";
import { CreateButton, List } from "@refinedev/antd";
import { Button, Input, Popconfirm, Select, Space, Table, Tag, Typography, message } from "antd";
import { useMemo, useState } from "react";
import { debounce } from "lodash";
import { apiUrl } from "@/consts";

type InviteStatus =
  | "PENDING"
  | "ACCEPTED"
  | "DECLINED"
  | "EXPIRED"
  | "CANCELLED"
  | null;

type Club = {
  id: string;
  name: string;
  slug: string;
  status: string;
  seasonFeePercent: string | null;
  /** Per-club override; null means the club inherits the platform default. */
  lottoCommissionPercent: string | null;
  onboardingCompletedAt: string | null;
  createdByUserID: string;
  ownerEmail: string | null;
  ownerName: string | null;
  inviteStatus: InviteStatus;
  onboardingComplete: boolean;
};

const INVITE_STATUS_COLOR: Record<string, string> = {
  PENDING: "gold",
  ACCEPTED: "success",
  DECLINED: "red",
  EXPIRED: "default",
  CANCELLED: "default",
};

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: "success",
  PENDING: "gold",
  INACTIVE: "default",
  SUSPENDED: "red",
};

const PAGE_SIZE = 20;

export function ClubsList() {
  const { show, create, edit } = useNavigation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"ACTIVE" | "SUSPENDED" | "ALL">("ACTIVE");

  const { data, isLoading, isFetching, refetch } = useCustom<{
    clubs: Club[];
    total: number;
    page: number;
    pageSize: number;
    globalLottoCommissionPercent?: number;
  }>({
    url: `${apiUrl}/clubs`,
    method: "get",
    config: {
      query: { q, status, page: String(page), pageSize: String(pageSize) },
    },
  });

  const clubs = data?.data?.clubs ?? [];
  const total = data?.data?.total ?? 0;
  const globalLottoPercent = data?.data?.globalLottoCommissionPercent ?? null;

  const { mutate: impersonateMutate } = useCustomMutation();
  const { mutate: deleteMutate } = useCustomMutation();
  const { mutate: statusMutate } = useCustomMutation();

  function handleSetStatus(id: string, status: "ACTIVE" | "SUSPENDED") {
    statusMutate(
      {
        url: `${apiUrl}/clubs/${id}/status`,
        method: "patch",
        values: { status },
      },
      {
        onSuccess: () => {
          message.success(status === "ACTIVE" ? "Club activated" : "Club deactivated");
          refetch();
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ?? err?.message ?? "Failed to update status"
          ),
      }
    );
  }

  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPage(1);
        setQ(value);
      }, 500),
    []
  );

  function handleDelete(id: string) {
    deleteMutate(
      {
        url: `${apiUrl}/clubs/${id}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success("Club deleted");
          refetch();
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error ?? err?.message ?? "Failed to delete club"),
      }
    );
  }

  function handleImpersonate(id: string) {
    impersonateMutate(
      {
        url: `${apiUrl}/clubs/${id}/impersonate`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (resp: any) => {
          const redirectUrl = resp?.data?.redirectUrl;
          if (redirectUrl) {
            window.location.assign(redirectUrl);
          } else {
            message.error("No redirect URL returned");
          }
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error ?? err?.message ?? "Failed to impersonate"),
      }
    );
  }

  return (
    <List
      headerButtons={<CreateButton onClick={() => create("clubs")}>Create club</CreateButton>}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by club name…"
          style={{ width: 360, maxWidth: "100%" }}
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
          onSearch={(value) => handleSearch(value)}
        />
        <Select
          aria-label="Club status"
          value={status}
          style={{ width: 180 }}
          options={[
            { value: "ACTIVE", label: "Active clubs" },
            { value: "SUSPENDED", label: "Deactivated clubs" },
            { value: "ALL", label: "All clubs" },
          ]}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
      </Space>
      <Table
        loading={isLoading || isFetching}
        dataSource={clubs}
        rowKey="id"
        pagination={{
          current: page,
          pageSize,
          total,
          showTotal: (t) => `${t} clubs`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
        }}
        columns={[
          {
            title: "Name",
            dataIndex: "name",
            render: (value: string, record: Club) => (
              <Button type="link" style={{ padding: 0 }} onClick={() => show("clubs", record.id)}>
                {value}
              </Button>
            ),
          },
          {
            title: "Owner",
            render: (_: any, record: Club) => (
              <Space direction="vertical" size={0}>
                <span>{record.ownerName ?? "—"}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {record.ownerEmail ?? "—"}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "Membership commission %",
            dataIndex: "seasonFeePercent",
            render: (value: string | null) => (value != null ? `${value}%` : "Default"),
          },
          {
            title: "Lotto commission %",
            dataIndex: "lottoCommissionPercent",
            render: (value: string | null) =>
              value != null ? (
                <Space size={4}>
                  <span>{Number(value)}%</span>
                  <Tag color="blue">Override</Tag>
                </Space>
              ) : (
                <Typography.Text type="secondary">
                  {globalLottoPercent != null ? `Default (${globalLottoPercent}%)` : "Default"}
                </Typography.Text>
              ),
          },
          {
            title: "Status",
            dataIndex: "status",
            render: (value: string) => (
              <Tag color={STATUS_COLOR[value] ?? "default"}>{value === "SUSPENDED" ? "Deactivated" : value}</Tag>
            ),
          },
          {
            title: "Invite status",
            dataIndex: "inviteStatus",
            render: (value: InviteStatus) =>
              value ? (
                <Tag color={INVITE_STATUS_COLOR[value] ?? "default"}>{value}</Tag>
              ) : (
                "—"
              ),
          },
          {
            title: "Onboarding",
            dataIndex: "onboardingComplete",
            render: (value: boolean) =>
              value ? (
                <Tag color="success">Complete</Tag>
              ) : (
                <Tag color="processing">In progress</Tag>
              ),
          },
          {
            title: "Actions",
            render: (_: any, record: Club) => (
              <Space>
                <Button size="small" onClick={() => show("clubs", record.id)}>
                  View
                </Button>
                <Button size="small" onClick={() => edit("clubs", record.id)}>
                  Edit
                </Button>
                <Button size="small" onClick={() => handleImpersonate(record.id)}>
                  Login as club
                </Button>
                {/* Deactivate hides the club from the app directory and its
                    public landing (both gate on status = ACTIVE); reversible. */}
                {record.status === "ACTIVE" ? (
                  <Popconfirm
                    title="Deactivate this club?"
                    description="Hides it from the app directory and its public landing page. No data is deleted — reversible."
                    onConfirm={() => handleSetStatus(record.id, "SUSPENDED")}
                    okText="Deactivate"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button size="small" danger>
                      Deactivate
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="Reactivate this club?"
                    description="Makes it visible in the app directory and its public landing page again."
                    onConfirm={() => handleSetStatus(record.id, "ACTIVE")}
                    okText="Activate"
                    cancelText="Cancel"
                  >
                    <Button size="small">Activate</Button>
                  </Popconfirm>
                )}
                {/* Deletable only while the owner invite is still un-accepted
                    (admin-created clubs); the backend enforces the same guard. */}
                {record.inviteStatus != null && record.inviteStatus !== "ACCEPTED" && (
                  <Popconfirm
                    title="Delete this club?"
                    description="Removes the club, its invitation, and the pending owner account."
                    onConfirm={() => handleDelete(record.id)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
    </List>
  );
}
